"use client";

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal } from "./modal";
import { Button } from "./ui/button";

/**
 * Reusable destructive-action confirmation modal.
 *
 * Pulled out of the DataTable so any page can use it for one-off
 * destructive flows (e.g. delete-all-from-detail-modal). Uses the
 * canonical Button primitives so action hierarchy + spinner + disabled
 * states match every other modal in the app.
 *
 * `onConfirm` may be async; the modal stays open with a spinner until
 * it resolves, then closes. If it throws, the error message is shown
 * inline and the modal stays open so the operator can retry or cancel.
 */
export function ConfirmModal({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  destructive = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      setBusy(false);
      onClose();
    } catch (e: any) {
      setBusy(false);
      setErr(e?.message ?? "Action failed");
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            size="lg"
            onClick={run}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {destructive ? (
          <div className="flex items-start gap-2 rounded-lg border border-status-critical/30 bg-status-critical/5 p-3">
            <AlertTriangle size={14} className="text-status-critical mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-fg-dim">{body}</div>
          </div>
        ) : (
          <div className="text-[12.5px] text-fg-dim">{body}</div>
        )}
        {err && <div className="text-[12px] text-status-critical">{err}</div>}
      </div>
    </Modal>
  );
}
